import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Label, SubmitBtn, TextInput } from "../styled/auth";
import { useTranslation } from "react-i18next";
import Loading from "../utilities/Loading";
import styled from "styled-components";
import { handleCompanyCode } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";
const CompanyLoginCard = styled(Paper)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  margin: 40px 20px;
  justifyContent: "center";
  alignItems: "center";
  gap: 32px;
  width: {
    xs: "calc(100% - 40px)",
    sm: "450px",
    md: "450px",
    lg: "420px",
  },
  minHeight: {
    xs: "auto",
    sm: "400px",
    md: "400px",
    lg: "450px",
  },
  borderRadius: {
    xs: "20px",
    sm: "24px",
    md: "24px",
    lg: "28px",
  },
  padding: {
    xs: "32px 24px",
    sm: "40px 32px",
    md: "40px 32px",
    lg: "48px 40px",
  },
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 480px) {
    margin: 20px 16px;
    width: "calc(100% - 32px)";
    padding: "24px 20px";
    gap: "24px";
  }
`;

const HeaderContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const FormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const InputGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
`;
export default function CompanyLogin(props) {
  const { setStep, setLoading, loading = false } = props;
  const { t } = useTranslation("auth");
  const schema = yup
    .object({
      companyCode: yup.string().required(t("CompanyLogin.error")),
    })
    .required();
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      companyCode: "",
    },
    resolver: yupResolver(schema),
  });
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = methods;
  const dispatch = useDispatch();
  const submitForm = (data) => {
    setLoading(true);
    dispatch(
      handleCompanyCode({
        code: data.companyCode,
        setLoading: setLoading,
        setStep: setStep,
      })
    )
      .then(unwrapResult)
      .then((data) => {
        setLoading(false);
        if (data === undefined) {
          setError("companyCode", { message: t("CompanyLogin.error_invalid") });
          setLoading(false);
        }
      });
  };

  return (
    <CompanyLoginCard elevation={0}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitForm)}>
          <HeaderContainer>
            <Typography 
              variant="h3" 
              sx={{ 
                color: "#1e293b", 
                fontWeight: 700, 
                fontSize: { xs: "28px", sm: "32px", md: "36px" },
                lineHeight: 1.2,
                textAlign: "center"
              }}
            >
              {t("CompanyLogin.title")}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: "#64748b", 
                fontWeight: 500,
                textAlign: "center",
                maxWidth: "300px",
                lineHeight: 1.5
              }}
            >
              {t("CompanyLogin.subtitle")}
            </Typography>
          </HeaderContainer>

          <FormContainer>
            <InputGroup>
              <Label htmlFor="companyCode">{t("CompanyLogin.label")}</Label>
              <TextInput
                id="companyCode"
                name="companyCode"
                variant="outlined"
                placeholder={t("CompanyLogin.placeholder")}
                {...register("companyCode")}
                error={!!errors.companyCode}
                helperText={errors.companyCode?.message}
              />
            </InputGroup>
          </FormContainer>

          <ButtonContainer>
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? (
                <Loading isButtonLoading={true} buttonText={t("CompanyLogin.next")} spinnerType="2" />
              ) : (
                t("CompanyLogin.next")
              )}
            </SubmitBtn>
          </ButtonContainer>
        </form>
      </FormProvider>
    </CompanyLoginCard>
  );
}
